import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { dateIt } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { NotebookPen, Trash2, Send } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export function DiarioCommessa({ commessaId }) {
  const qc = useQueryClient();
  const { user, isAdmin } = useAuth();
  const [data, setData] = useState(today());
  const [testo, setTesto] = useState("");

  const { data: note = [], isLoading } = useQuery({
    queryKey: ["diario", commessaId],
    queryFn: async () => (await api.get(`/commesse/${commessaId}/diario`)).data,
  });

  const addMut = useMutation({
    mutationFn: async () => (await api.post(`/commesse/${commessaId}/diario`, { data, testo })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["diario", commessaId] });
      setTesto("");
      toast.success("Nota salvata");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/diario/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["diario", commessaId] });
      toast.success("Nota eliminata");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const grouped = note.reduce((acc, n) => {
    (acc[n.data] = acc[n.data] || []).push(n);
    return acc;
  }, {});

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-6">
      <div className="grid sm:grid-cols-6 gap-3 items-end">
        <div className="space-y-1.5">
          <Label>Giorno</Label>
          <Input type="date" value={data} onChange={(e) => setData(e.target.value)} className="bg-white" data-testid="diario-data-input" />
        </div>
        <div className="space-y-1.5 sm:col-span-4">
          <Label>Cosa è stato fatto oggi</Label>
          <Textarea
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            placeholder="es. Montate 12 m di tubazione DN80, saldati 6 giunti, attesa materiale flange"
            rows={2}
            className="bg-white resize-none"
            data-testid="diario-testo-input"
          />
        </div>
        <Button
          onClick={() => (testo.trim() ? addMut.mutate() : toast.error("Scrivi il testo della nota"))}
          disabled={addMut.isPending}
          className="h-10"
          data-testid="diario-add-button"
        >
          <Send className="h-4 w-4 mr-1" /> Salva
        </Button>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-slate-400">Caricamento…</div>
      ) : note.length === 0 ? (
        <div className="py-10 text-center text-slate-400" data-testid="diario-empty">
          <NotebookPen className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          Nessuna nota nel diario. Scrivi la prima!
        </div>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([giorno, items]) => (
            <div key={giorno} data-testid={`diario-giorno-${giorno}`}>
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-xs font-semibold text-primary bg-orange-50 border border-orange-200 rounded-md px-2 py-0.5">
                  {dateIt(giorno)}
                </span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>
              <div className="space-y-2 pl-1 border-l-2 border-orange-100 ml-2">
                {items.map((n) => (
                  <div key={n.id} className="ml-3 bg-slate-50 border border-slate-100 rounded-lg px-4 py-3 flex items-start justify-between gap-3" data-testid={`diario-nota-${n.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{n.testo}</p>
                      <div className="text-xs text-slate-400 mt-1">{n.autore_nome}</div>
                    </div>
                    {(isAdmin || n.autore_id === user?.id) && (
                      <button onClick={() => delMut.mutate(n.id)} className="text-slate-300 hover:text-red-600 shrink-0" data-testid={`diario-delete-${n.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
